import { useEffect } from "react";
import { createPortal } from "react-dom";
import Footer from "./footer.jsx";
import { useScrollLock } from "./primitives.jsx";
import { useMeta } from "../seo.js";
import { ROUTE_META } from "../routeMeta.js";
import { MAIN_HOST, SITE_URL } from "../siteAddress.js";

// Hall of Fame manager Tony La Russa's foreword to Greg's memoir. La Russa
// managed Greg with the White Sox, traded him to Kansas City before the 1982
// season, and faced him as an opponent for years after — so the foreword is
// also a credibility asset: a famous name vouching for the book, reproduced in
// full (Greg holds the rights). Verbatim from the manuscript.
//
// Same content, two shells (mirrors the shave story): <ForewordPage> is the
// standalone, indexable route served to direct visits / crawlers / shared
// links; <ForewordModal> is the in-app overlay so reading it from the homepage
// never reloads the page. The text lives once, in <ForewordStory>, so the two
// can't drift.

const BOOK_COVER =
  "https://m.media-amazon.com/images/S/compressed.photo.goodreads.com/books/1554949076i/45018799.jpg";

const ARTICLE_LD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Foreword by Tony La Russa",
  description: ROUTE_META.foreword.description,
  image: `${SITE_URL}/assets/greg-yankees.jpg`,
  datePublished: "2026-06-03",
  author: { "@type": "Person", name: "Tony La Russa" },
  publisher: { "@id": `${SITE_URL}/#greg` },
  mainEntityOfPage: SITE_URL + ROUTE_META.foreword.path,
  about: ["Greg Pryor", "Chicago White Sox", "Kansas City Royals", "1985 World Series"],
  mentions: {
    "@type": "Book",
    "@id": `${SITE_URL}/#book`,
    name: "The Day The Yankees Made Me Shave",
  },
};

// Per-route <head> + Article structured data, shared by the page and the modal
// so an open foreword always reflects the right title/canonical/OG and schema.
function useForewordSeo() {
  useMeta({ ...ROUTE_META.foreword, ogType: "article" });
  useEffect(() => {
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify(ARTICLE_LD);
    document.head.appendChild(ld);
    return () => ld.remove();
  }, []);
}

// The foreword content — no page chrome. `onBuy`, when supplied (modal
// context), intercepts the CTA so it closes the overlay and returns to the book
// section in place instead of doing a full navigation.
function ForewordStory({ onBuy }) {
  return (
    <article className="fwd-article">
      <header className="fwd-hero">
        <div className="fwd-hero-top">
          <span className="fwd-monogram mono" aria-hidden>
            TLR
          </span>
          <span className="mono fwd-kicker">Foreword · The Day The Yankees Made Me Shave</span>
        </div>
        <h1 className="display fwd-title">Foreword by Tony La Russa</h1>
        <p className="fwd-dek">
          The Hall of Fame manager had Greg in Chicago, decided he didn&rsquo;t fit the plans, and
          traded him away — then spent years watching him get even, twice on walk-off home runs. His
          foreword to the book, in full.
        </p>
        <div className="fwd-stats">
          <div className="fwd-stat">
            <span className="fwd-stat-num display">27</span>
            <span className="fwd-stat-label mono">stories · one per out</span>
          </div>
          <div className="fwd-stat">
            <span className="fwd-stat-num display">10</span>
            <span className="fwd-stat-label mono">MLB seasons</span>
          </div>
          <div className="fwd-stat">
            <span className="fwd-stat-num display">1985</span>
            <span className="fwd-stat-label mono">World Series ring</span>
          </div>
          <div className="fwd-stat">
            <span className="fwd-stat-num display">HOF</span>
            <span className="fwd-stat-label mono">wrote this foreword</span>
          </div>
        </div>
      </header>

      <div className="fwd-body">
        <p>
          My first impression is that the set up for Greg Pryor&rsquo;s book,{" "}
          <em>The Day The Yankees Made Me Shave</em>, is creative and smart. It includes
          twenty-seven stories that match the 27 outs needed to win a regulation game. From
          Greg&rsquo;s career filled with many interesting possibilities, the ones chosen for his
          first book make for a Major League read.
        </p>

        <p>
          In the Preface, Greg&rsquo;s former agent, Steve Greenberg, rightfully claims that
          Greg&rsquo;s career went well beyond labels, like utility man or journeyman which too
          often are meant disrespectfully. Our teams believed that a utility man made special
          contributions because of his versatility and a journeyman meant that several clubs
          understood his value. A player does not earn 10 years in the Major Leagues unless he is
          productive.
        </p>

        <p>
          In his 27 chapters, Greg mixes a variety of professional experiences and personal
          comments. He celebrates and suffers. He is feisty and funny. He is opinionated and ready
          to disagree and correct the mistaken; and as you will read, it was often with me. Above
          all, he demonstrates the toughness and determination of his father George H. Pryor. His
          dad&rsquo;s example led Greg to success by persevering through setbacks at every step in
          his path to the Majors. It all comes together to give the reader a rare inside look at the
          game.
        </p>

        <p>
          From this inside perspective, all the stories offer their own unique insights. Often there
          were also references to special people that I really enjoyed. There was Greg, catching
          balls hit by a fungo bat in the hands of Yogi Berra and Elston Howard; Dave Parker&rsquo;s
          explosive speed, surprising Greg by beating out a routine grounder for a hit; Hall of
          Famers Hank Greenberg and Jimmy Connors playing tennis; Gaylord Perry&rsquo;s
          &ldquo;unique&rdquo; pitch causing ejections of Earl Weaver and Reggie Jackson; the
          relationship switch between Greg and Ed Farmer from an opponent&rsquo;s headhunting pitch
          to a teammate and valued friend; and two behind the scenes stars: Al Zych, the Royals
          equipment manager, and Nick Roberts, a host at the White Sox Bards Room.
        </p>

        <p>
          There are two more examples that deserve special attention because they represent exactly
          what professional baseball&rsquo;s first priority is supposed to be. Teams compete for the
          best score and some players step forward to lead their teams to championships. Two of the
          best leaders of our time are a part of chapters. A &ldquo;disgusted&rdquo; Eddie Murray
          watched Greg defeat his Orioles in 1984 with Greg&rsquo;s second walk-off home run. Hal
          McRae receives recognition for his leadership role in the Royals&rsquo; 1985 World Series
          championship.
        </p>

        <p>
          As part of this Foreword, Greg encouraged me to add comments to chapters when we were
          teammates or opponents. He missed our first &ldquo;interaction&rdquo;—which was part of
          his best Major League game. On September 8, 1978, the Chicago White Sox honored Don
          Kessinger with a special night at Comiskey Park. The final score was a 3-2 win, and Greg
          drove in all three runs with two home runs. I was the first base coach and observed that
          he had better than average power for an infielder. An interesting side note from that game
          was the effect of Greg taking his one and only &ldquo;greenie.&rdquo;
        </p>

        <p>
          The second thing Greg failed to mention was his first walk off homer on July 6, 1980,
          versus the Oakland A&rsquo;s which caused an epic Billy Martin locker room meltdown that
          is still talked about in Chicago. Greg appeared as a pinch hitter with the worst
          preparation. I remembered his power and their pitcher fit his hot zone but forgot to alert
          him to get ready. He had no time to get loose, and even had to borrow a bat. I messed up,
          but thankfully Greg made us a winner and covered my butt. It&rsquo;s true that you learn
          more from your mistakes than your successes. The lack of notice to Greg was an example.
        </p>

        <p>
          July 20, 1980, was the first and last time that an infielder on our team was part of the
          process of giving signs between catcher and pitcher—and Greg has that badge of honor. The
          Sox were playing the Brewers who were filled with outstanding players like Paul Molitor
          and Robin Yount. We had a rookie battery of Richard Dotson and Ricky Seilheimer. It was
          impossible to prepare them properly and unfair to them and our team, to ask them to
          compete against such a good lineup. We designed some help. Our pitching coach, Ron
          Schueler, from the dugout, sent signs to Greg at shortstop. Greg then gestured to
          Ricky—who put the fingers down for Richard&rsquo;s pitch. It was a certain kind of
          success. We kept it tied in the bottom of the eighth but eventually lost, 7-6. It&rsquo;s
          worth noting that, occasionally, Cal Ripken, Jr., with his high baseball IQ, would signal
          the catcher with a pitch to call.
        </p>

        <p>
          The game on October 4, 1980, ended up with both of us upset with each other. Greg started
          the game at third base. We were leading 4-2 going into the bottom of the seventh, and Greg
          was leading off. But I pinch hit Minnie Minoso.
        </p>

        <p>
          Our organization wanted to make a historical statement, and I supported it. Minnie&rsquo;s
          appearance at age 55 meant he would play in five decades! We were excited. Greg was not.
          To his credit, he was upfront with his claims that winning was the priority and gimmicks
          had no place in baseball. I appreciated his desire to play but not his remarks that he
          shares with readers.
        </p>

        <p>
          There was an issue about playing the game straight as against conceding to history. I
          chose history because our position was set in the standings and Minnie deserved the honor.
          Finally, I relied on the old manager adage: The manager has the office, and the player has
          the locker. I made my decision. Minnie hit, and we won 4-2.
        </p>

        <p>
          My most difficult experience with Greg covered January 1982 into the latter part of spring
          training. Our organization had decided that Greg did not fit into our plans. Managers
          learn that you will never survive unless the players trust you. One of the toughest tests
          is how you handle a conversation that is negative to the player. The phone call to Greg
          falls into that most difficult category. The truth was your only real option. The news was
          bad enough and on top of which, was Michelle&rsquo;s pregnancy with their first child. To
          his credit and consistent with his toughness, Greg came to camp and worked hard.
        </p>

        <p>
          His reward was a trade to the Kansas City Royals, and it <em>was</em> a reward. Several
          times in the book Greg describes the Kansas City winning attitude, teammates (especially
          George Brett and Dick Howser) and memorable experiences. The best of the latter was the
          1985 World Series Championship.
        </p>

        <blockquote className="fwd-pull">
          Greg&rsquo;s description of the path to the &ldquo;ring&rdquo; is outstanding.
        </blockquote>

        <p>
          Our final interaction was as opponents on August 26, 1984. It ended dramatically in
          Greg&rsquo;s favor. A hot Kansas City afternoon on artificial turf was punishing players
          on both teams, especially as the game continued into the 16th inning. Greg came to bat
          remembering that I gave up on him. I was thinking: don&rsquo;t walk him (not really). As
          he trotted around the bases after his walk-off home run, I didn&rsquo;t stay in the dugout
          to watch the finish of his revenge.
        </p>

        <p>
          Over the years in Chicago, Oakland and St. Louis, we worked to compete as a family. Each
          player was accountable for contributing his piece. Besides placing you in a position to
          win, it creates relationships that last forever.
        </p>

        <p>
          Greg was a key part of our White Sox family. Sincerity in your words and actions is a
          must. That&rsquo;s why my favorite words in his book spoke to his sincerity: &ldquo;I
          loved being nervous before games because it helped me get mentally prepared.&rdquo; Unless
          you care, you don&rsquo;t get nervous. Greg always cared.
        </p>

        <div className="fwd-sign">
          <span className="fwd-sign-name display">Tony La Russa</span>
          <span className="mono fwd-sign-role">Baseball Hall of Fame Manager</span>
        </div>
      </div>

      <aside className="fwd-cta">
        <a
          className="fwd-cta-cover"
          href="/#book"
          onClick={onBuy}
          aria-label="The Day The Yankees Made Me Shave — get the book"
        >
          <img src={BOOK_COVER} alt="The Day The Yankees Made Me Shave — book cover" />
        </a>
        <div className="fwd-cta-copy">
          <div className="mono fwd-cta-kicker">The stories he&rsquo;s talking about</div>
          <h2 className="display fwd-cta-title">Every chapter La Russa names is in the book.</h2>
          <p>
            The Kessinger night, the Minnie Minoso game, the 16th-inning walk-off in Kansas City —
            all twenty-seven of them are in <em>The Day The Yankees Made Me Shave</em>. Every copy
            signed by hand, in real ink.
          </p>
          <div className="fwd-cta-row">
            <a className="fwd-btn fwd-btn-primary" href="/#book" onClick={onBuy}>
              Get the signed book →
            </a>
          </div>
        </div>
      </aside>

      <p className="fwd-context">
        <strong>About this foreword:</strong> Tony La Russa managed Greg Pryor with the Chicago
        White Sox, traded him to the Kansas City Royals before the 1982 season, and faced him as an
        opponent for years after. He is a three-time World Series champion and a member of the
        Baseball Hall of Fame. He wrote this introduction for Greg&rsquo;s memoir.
      </p>
    </article>
  );
}

// Standalone route — served on a direct visit, a reload, a shared link, or a
// crawler. The foreword is the page's primary content.
export default function ForewordPage() {
  useForewordSeo();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <main className="fwd-page grain">
        <header className="fwd-nav">
          <a href="/" className="fwd-brand">
            <span className="fwd-brand-mark">GP</span>
            <span>Greg Pryor</span>
          </a>
          <a className="fwd-home-link mono" href="/">
            Back to the site
          </a>
        </header>
        <div className="fwd-page-body">
          <ForewordStory />
          <a className="mono fwd-back" href="/">
            ← Back to {MAIN_HOST}
          </a>
        </div>
      </main>
      <Footer />
    </>
  );
}

// In-app overlay — opened from the homepage so reading never reloads the page.
// Escape / backdrop / the close button all dismiss it (which restores the URL
// via the caller). The page beneath stays mounted, so closing returns the
// visitor to the exact spot they were reading from. Mirrors <ShaveModal>.
export function ForewordModal({ onClose }) {
  useForewordSeo();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock the page beneath the modal (iOS-safe); restores the scroll position
  // and forces a repaint on close so Safari can't leave the page blank.
  useScrollLock();

  // CTA inside the modal: close the overlay — no navigation, no reload. The
  // overlay is only opened from the callout in the homepage's book section, and
  // closing restores that exact scroll position, so the reader lands right back
  // on the book + its buy controls.
  const goToBook = (e) => {
    e.preventDefault();
    onClose();
  };

  return createPortal(
    <div
      className="fwd-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="fwd-modal-panel grain"
        role="dialog"
        aria-modal="true"
        aria-label="Foreword by Tony La Russa"
      >
        <button className="fwd-modal-close" onClick={onClose} aria-label="Close">
          <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" fill="none" />
          </svg>
        </button>
        <ForewordStory onBuy={goToBook} />
      </div>
    </div>,
    document.body,
  );
}

const forewordStyles = typeof document !== "undefined" ? document.createElement("style") : null;
if (forewordStyles) {
  forewordStyles.textContent = `
.fwd-page {
  min-height: 100vh;
  background: var(--navy-900);
  color: var(--bone);
}
.fwd-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px clamp(20px, 5vw, 64px);
  border-bottom: 1px solid var(--rule);
}
.fwd-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--bone);
  font-family: var(--serif);
  font-weight: 600;
  font-size: 1.125rem;
}
.fwd-brand-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--royal-blue);
  border: 1px solid var(--gold);
  font-family: var(--mono);
  font-size: 0.75rem;
  color: var(--bone);
}
.fwd-home-link {
  color: var(--gold-bright);
  text-decoration: none;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
}
.fwd-home-link:hover { color: var(--bone); }

.fwd-page-body {
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(48px, 9vw, 104px) clamp(20px, 6vw, 40px) 80px;
}
.fwd-article { color: var(--bone); }
.fwd-kicker {
  color: var(--gold);
  font-size: 0.75rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0 0 22px;
}
.fwd-title {
  font-size: clamp(2.375rem, 7vw, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.01em;
  color: var(--bone);
  margin: 0 0 28px;
}
.fwd-dek {
  font-family: var(--serif);
  font-size: clamp(1.1875rem, 2.4vw, 1.4375rem);
  line-height: 1.5;
  color: var(--bone-dim);
  margin: 0;
  max-width: 60ch;
}

.fwd-hero-top {
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 0 22px;
}
.fwd-hero-top .fwd-kicker { margin: 0; }
.fwd-monogram {
  flex: none;
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: var(--royal-blue);
  border: 1px solid var(--gold);
  color: var(--bone);
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  font-weight: 600;
}

/* Highlight tiles — the marquee facts, up top, scannable */
.fwd-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin: clamp(28px, 4vw, 38px) 0 0;
}
@media (max-width: 560px) {
  .fwd-stats { grid-template-columns: repeat(2, 1fr); }
}
.fwd-stat {
  display: flex;
  flex-direction: column;
  gap: 7px;
  padding: 16px 14px;
  border: 1px solid var(--rule-strong);
  border-radius: 10px;
  background: linear-gradient(160deg, rgba(0, 100, 194, 0.12), rgba(0, 70, 135, 0.03));
}
.fwd-stat-num {
  font-size: clamp(1.625rem, 4vw, 2.125rem);
  line-height: 1;
  color: var(--gold-bright);
}
.fwd-stat-label {
  font-size: 0.5625rem;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  line-height: 1.35;
  color: var(--bone-dim);
}

.fwd-body { margin-top: clamp(40px, 7vw, 60px); }
.fwd-body > p {
  font-family: var(--serif);
  font-size: 1.1875rem;
  line-height: 1.72;
  color: var(--bone-dim);
  margin: 0 0 28px;
}
.fwd-body em { color: var(--bone); font-style: italic; }

.fwd-pull {
  position: relative;
  margin: 44px 0;
  padding: 26px 30px 26px 34px;
  border: 1px solid var(--rule-strong);
  border-left: 3px solid var(--gold);
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(189, 155, 96, 0.09), rgba(189, 155, 96, 0.02));
  font-family: var(--serif);
  font-size: clamp(1.375rem, 3.2vw, 1.75rem);
  line-height: 1.34;
  font-style: italic;
  color: var(--bone);
}
.fwd-pull::before {
  content: "“";
  position: absolute;
  top: -6px;
  left: 14px;
  font-family: var(--serif);
  font-size: 3.5rem;
  line-height: 1;
  color: var(--gold);
  opacity: 0.45;
}

.fwd-sign {
  margin-top: 44px;
  padding-top: 28px;
  border-top: 1px solid var(--rule);
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fwd-sign-name {
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  line-height: 1;
  color: var(--bone);
}
.fwd-sign-role {
  color: var(--gold);
  font-size: 0.6875rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
}

.fwd-cta {
  display: grid;
  grid-template-columns: 176px 1fr;
  gap: clamp(24px, 4vw, 40px);
  align-items: center;
  margin: clamp(44px, 7vw, 64px) 0 48px;
  padding: clamp(26px, 4vw, 38px);
  background: var(--navy-800);
  border: 1px solid var(--rule-strong);
  border-radius: 14px;
}
.fwd-cta-cover {
  display: block;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
  transition: transform 220ms ease;
}
.fwd-cta-cover:hover { transform: translateY(-4px); }
.fwd-cta-cover img { display: block; width: 100%; height: auto; }
.fwd-cta-kicker {
  color: var(--gold);
  font-size: 0.6875rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0 0 12px;
}
.fwd-cta-title {
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  line-height: 1.12;
  color: var(--bone);
  margin: 0 0 14px;
}
.fwd-cta-copy > p {
  font-family: var(--serif);
  font-size: 1rem;
  line-height: 1.6;
  color: var(--bone-dim);
  margin: 0 0 22px;
}
.fwd-cta-copy em { color: var(--bone); font-style: italic; }
.fwd-cta-row { display: flex; flex-wrap: wrap; gap: 12px; }
.fwd-btn {
  display: inline-block;
  padding: 13px 22px;
  font-family: var(--mono);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-decoration: none;
  border-radius: 7px;
  transition: background 200ms, color 200ms, border-color 200ms;
}
.fwd-btn-primary {
  background: var(--gold);
  color: var(--navy-900);
  border: 1px solid var(--gold);
}
.fwd-btn-primary:hover { background: var(--gold-bright); border-color: var(--gold-bright); }

.fwd-context {
  border-top: 1px solid var(--rule);
  padding-top: 32px;
  font-family: var(--serif);
  font-size: 1rem;
  line-height: 1.66;
  color: var(--bone-dim);
  margin: 0;
}
.fwd-context strong { color: var(--bone); }
.fwd-back {
  display: inline-block;
  margin-top: 28px;
  color: var(--gold-bright);
  text-decoration: none;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
}
.fwd-back:hover { color: var(--bone); }

/* --- In-app overlay — identical shell to the shave story modal --- */
.fwd-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(3, 8, 18, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(16px, 5vh, 56px) 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: fwd-fade 200ms ease;
}
@keyframes fwd-fade { from { opacity: 0; } to { opacity: 1; } }
.fwd-modal-panel {
  position: relative;
  width: 100%;
  max-width: 720px;
  background: var(--navy-900);
  border: 1px solid var(--rule-strong);
  border-radius: 16px;
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.6);
  padding: clamp(28px, 5vw, 56px) clamp(22px, 5vw, 56px) clamp(32px, 5vw, 56px);
  animation: fwd-rise 240ms cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes fwd-rise { from { transform: translateY(14px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.fwd-modal-panel .fwd-title { font-size: clamp(2rem, 6vw, 3.25rem); }
.fwd-modal-panel .fwd-body { margin-top: clamp(32px, 5vw, 48px); }
.fwd-modal-close {
  position: sticky;
  top: 0;
  float: right;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  margin: -8px -8px 0 16px;
  border-radius: 50%;
  border: 1px solid var(--rule-strong);
  background: var(--navy-800);
  color: var(--bone-dim);
  cursor: pointer;
  transition: color 180ms, border-color 180ms, background 180ms;
}
.fwd-modal-close:hover { color: var(--bone); border-color: var(--gold); background: var(--navy-700); }

@media (max-width: 620px) {
  .fwd-cta { grid-template-columns: 1fr; text-align: center; }
  .fwd-cta-cover { max-width: 168px; margin: 0 auto; }
  .fwd-cta-row { justify-content: center; }
}
`;
  document.head.appendChild(forewordStyles);
}
